import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { memberCheckApi, type MemberOutResultRow } from '../../api/memberCheck.js';
import { ErrorBox } from '../../components/ui.js';
import { money, num } from '../../lib/format.js';

/**
 * 회원 탈퇴 전 불이익(적립금·주문이력) 확인.
 *
 * 원본: member_out.php + member_out_exec.php
 * 레거시 메뉴명은 "인터넷 진흥원(탈퇴처리)" - 휴면계정을 정리하기 전, 적립금이나
 * 주문이력이 남아 있는지 CS 가 미리 확인하던 도구다. 엑셀에서 "이름 (탭) 생년월일
 * (탭) 휴대폰번호 (탭) 이메일주소" 를 그대로 붙여넣으면, 한 줄마다 이메일(또는
 * 휴대폰) 일치로 회원을 찾아 상태를 보여준다.
 *
 * 원본은 결과를 다시 텍스트박스(탭 구분)로만 보여줬다. 여기서는 표로 보여주되,
 * 원본 형식 그대로의 탭 구분 결과도 "복사" 로 그대로 꺼낼 수 있게 뒀다 - 이
 * 형식을 그대로 옮겨 붙이는 다른 절차가 있을 수 있어서다.
 */
export function MemberOutCheckPage() {
  const [text, setText] = useState('');
  const [copied, setCopied] = useState(false);

  const check = useMutation({
    mutationFn: () => memberCheckApi.check(text),
  });

  async function copyTsv() {
    if (!check.data) return;
    try {
      await navigator.clipboard.writeText(check.data.tsv);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 클립보드를 못 쓰는 환경이면 아래 표를 보고 손으로 옮기면 된다.
    }
  }

  const rows = check.data?.rows ?? [];
  const foundCount = rows.filter((r) => r.found).length;

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            회원 탈퇴 전 불이익 확인
            <span className="badge badge--danger">개인정보 조회</span>
          </h1>
          <p className="page-desc">
            엑셀에서 <strong>이름, 생년월일, 휴대폰번호, 이메일주소</strong> 네 칸을 그대로
            붙여넣으세요 (한 줄에 한 명). 이메일 또는 휴대폰번호로 회원을 찾아 적립금·주문이력을
            보여줍니다.
          </p>
        </div>
      </header>

      <form
        className="panel"
        onSubmit={(e) => {
          e.preventDefault();
          check.mutate();
        }}
      >
        <textarea
          className="input"
          style={{ width: '100%', height: 240, fontFamily: 'monospace' }}
          placeholder={'홍길동\t1990-01-01\t010-1234-5678\ta@example.com'}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="toolbar" style={{ marginTop: 10 }}>
          <button type="submit" className="btn" disabled={check.isPending || text.trim() === ''}>
            {check.isPending ? '확인 중…' : '체크'}
          </button>
          {check.data && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => void copyTsv()}>
              {copied ? '복사 완료' : '탭 구분 결과 복사'}
            </button>
          )}
        </div>
      </form>

      {check.isError && <ErrorBox error={check.error} />}

      {check.data && (
        <>
          <div className="summary" style={{ marginTop: 16 }}>
            <div className="summary__item">
              <span className="summary__label">넣은 줄</span>
              <strong className="summary__value">{num(check.data.requested)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">찾은 회원</span>
              <strong className="summary__value">{num(foundCount)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">비회원</span>
              <strong className="summary__value">{num(check.data.requested - foundCount)}</strong>
            </div>
          </div>

          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>입력 이름</th>
                  <th>입력 이메일</th>
                  <th>입력 휴대폰</th>
                  <th style={{ textAlign: 'center' }}>판정</th>
                  <th>회원 아이디</th>
                  <th>회원명</th>
                  <th style={{ textAlign: 'right' }}>적립금</th>
                  <th>가입일</th>
                  <th>마지막로그인</th>
                  <th>최종주문일</th>
                  <th style={{ textAlign: 'right' }}>로그인횟수</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <MemberOutRow key={i} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function MemberOutRow({ row }: { row: MemberOutResultRow }) {
  return (
    <tr>
      <td>{row.inputName || '-'}</td>
      <td>{row.inputEmail || '-'}</td>
      <td>{row.inputPhone || '-'}</td>
      <td style={{ textAlign: 'center' }}>
        <span className={row.found ? 'td-danger' : 'td-muted'}>{row.status}</span>
      </td>
      {row.found ? (
        <>
          <td>{row.memberId}</td>
          <td>{row.memberName || '-'}</td>
          <td style={{ textAlign: 'right' }}>{money(row.mempoints)}</td>
          <td>{row.signdate || '-'}</td>
          <td>{row.lastlogin || '-'}</td>
          <td>{row.lastorder || '-'}</td>
          <td style={{ textAlign: 'right' }}>{num(row.logincount)}</td>
        </>
      ) : (
        <td colSpan={7} className="td-muted">
          일치하는 회원이 없습니다.
        </td>
      )}
    </tr>
  );
}
